import * as React from "react";
import { Block } from "baseui/block";
import { SIZE, ROLE } from "baseui/modal";

import { StatefulTabs, Tab } from "kanopi-components/Tabs";
import {
  HeadingSmall,
  LabelLarge,
  ParagraphXSmall,
} from "kanopi-components/typography";
import { Button } from "kanopi-components/Button";
import { Modal } from "kanopi-components/Modal";
import CheckCircle from "kanopi-components/icons/CheckCircle";
import DeleteCircle from "kanopi-components/icons/DeleteCircle";
import Check from "baseui/icon/check";
import Delete from "baseui/icon/delete";

import { useTrackViewCoverLimitations } from "./useTracking";
import {
  CoverLimitationsList,
  CoverLimitationsGroupedList,
} from "./CoverLimitationsList";
import { getInclusionsAndExclusions } from "./utils";

type TCoverLimitationsProps = {
  limitations: TCoverLimitation[];
  limit?: number;
};

const CoverDisclaimer = () => (
  <ParagraphXSmall>
    This summary of cover is a guide only. The examples provided are only some of the
    conditions, exclusions and limits in this policy. You must read the policy
    documentation for all information about this policy.
  </ParagraphXSmall>
);

const CoverLimitations: React.FC<TCoverLimitationsProps> = ({
  limitations,
  limit = 5,
}) => {
  const trackViewCoverLimitations = useTrackViewCoverLimitations();
  const [isOpen, setIsOpen] = React.useState(false);
  const [inclusions, exclusions] = getInclusionsAndExclusions(limitations);
  const viewMore = inclusions?.length > limit || exclusions?.length > limit;

  const handleOpenModal = () => {
    setIsOpen(true);
    trackViewCoverLimitations();
  };

  if (!inclusions?.length && !exclusions?.length) {
    return null;
  }

  return (
    <>
      <HeadingSmall marginTop="scale1200" marginBottom={0} center>
        What&rsquo;s covered and what&rsquo;s not?
      </HeadingSmall>
      <Block
        display="flex"
        flexDirection={["column", "column", "row"]}
        marginTop="scale1000"
        justifyContent="space-between"
        paddingLeft={["scale100", "scale500", "scale1400"]}
        paddingRight={["scale100", "scale500", "scale1400"]}
      >
        {inclusions?.length > 0 && (
          <Block flex={1} paddingLeft={["scale100", "scale100", "scale1000"]}>
            <LabelLarge marginTop={0} marginBottom="scale800">
              Included
            </LabelLarge>
            <CoverLimitationsList
              items={inclusions.slice(0, limit)}
              artwork={<CheckCircle size={24} color="positive300" />}
            />
          </Block>
        )}
        {exclusions?.length > 0 && (
          <Block flex={1}>
            <LabelLarge
              marginTop={["scale1200", "scale1200", 0]}
              marginBottom="scale800"
            >
              Excluded
            </LabelLarge>
            <CoverLimitationsList
              items={exclusions.slice(0, limit)}
              artwork={<DeleteCircle size={24} color="negative300" />}
            />
          </Block>
        )}
      </Block>
      {viewMore && (
        <>
          <Block
            marginTop="scale1200"
            marginBottom={0}
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <Button
              size="mini"
              shape="pill"
              kind="primary"
              onClick={handleOpenModal}
              overrides={{
                BaseButton: {
                  style: {
                    textTransform: "uppercase",
                  },
                },
              }}
              data-testid="coverLimitationsViewAllButton"
            >
              View more
            </Button>
          </Block>
          <Modal
            onClose={() => setIsOpen(false)}
            closeable
            isOpen={isOpen}
            animate
            autoFocus
            size={SIZE.full}
            role={ROLE.dialog}
          >
            <Modal.Header />
            <Modal.Body $style={{ marginTop: "64px" }}>
              <StatefulTabs>
                <Tab title="Included">
                  <CoverLimitationsGroupedList
                    items={inclusions}
                    artwork={<Check size={24} color="positive300" />}
                  />
                  <CoverDisclaimer />
                </Tab>
                <Tab title="Excluded">
                  <CoverLimitationsGroupedList
                    items={exclusions}
                    artwork={<Delete size={24} color="negative300" />}
                  />
                  <CoverDisclaimer />
                </Tab>
              </StatefulTabs>
            </Modal.Body>
          </Modal>
        </>
      )}
    </>
  );
};

export { CoverLimitations };
